// D15 shared-content scanner (frame 5f / 5l). Walks a thread's messages and
// buckets every shared attachment into the four rail sections — Media ·
// Documents · Links · Business Cards — reverse-chronological, each entry
// carrying preview/icon, name, date, sender and the id of the message it came
// from (so a click can jump to it) plus a download url where one exists.
//
// Honesty note (Bible D15 "ehrlich paginieren statt alles laden"): the scanner
// reads the messages the client currently holds (channel.state.messages). Older
// shares surface as history pages load; it never bulk-fetches the whole channel
// just to populate a rail. This is intentional and documented, not a bug.

import { KNOWN_CARD_ATTACHMENT_TYPES, isPlatformCardLink } from "../chat/noRawLinks";

export const SHARED_SECTIONS = {
  MEDIA: "media",
  DOCUMENTS: "documents",
  LINKS: "links",
  BUSINESS_CARDS: "business_cards",
};

const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "";

// "9 Jul" — compact day+month, matching the 5f entry meta line.
const shortDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

// Human file size ("1.2 MB", "84 KB"). Bytes in → the 5f "· 1.2 MB" suffix.
export const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

// Uppercase file extension from a name or mime ("pdf" → "PDF"), for the doc chip.
export const fileKind = (att) => {
  const name = att?.title || att?.fallback || att?.name || "";
  const extMatch = String(name).match(/\.([a-z0-9]{1,5})$/i);
  if (extMatch) return extMatch[1].toUpperCase();
  const mime = String(att?.mime_type || "");
  const mimeTail = mime.split("/")[1];
  if (mimeTail) return mimeTail.replace(/[^a-z0-9]/gi, "").slice(0, 4).toUpperCase();
  return "FILE";
};

// A share/card attachment → the label the Links section shows. Uses the card's
// own title fields; never a raw URL (D7 family). Falls back to a generic label.
const cardLinkTitle = (att) => {
  return (
    att?.title ||
    att?.player_name ||
    att?.name ||
    att?.listing_title ||
    att?.club_name ||
    "Shared profile"
  );
};

const isImageAttachment = (att) =>
  att?.type === "image" || (att?.mime_type || "").startsWith("image/");
const isVideoAttachment = (att) =>
  att?.type === "video" || (att?.mime_type || "").startsWith("video/");
const isBusinessCard = (att) =>
  att?.type === "business-card" || att?.type === "business_card";
const isFileAttachment = (att) =>
  att?.type === "file" ||
  (Boolean(att?.asset_url) && !isImageAttachment(att) && !isVideoAttachment(att));

// Platform card-link tokens inside message text (a shared /search/player URL,
// etc.) become Links entries too, matching the in-chat card family.
const ABS_URL = /https?:\/\/[^\s<>()]+/gi;

/**
 * Scan a message list into the four D15 sections.
 *
 * @param {Array} messages   channel.state.messages (chronological)
 * @param {object} [opts]
 * @param {string} [opts.selfId]   the viewer's Stream id → sender reads "You"
 * @returns {{media:Array, documents:Array, links:Array, business_cards:Array}}
 */
export function scanSharedContent(messages, { selfId } = {}) {
  const media = [];
  const documents = [];
  const links = [];
  const businessCards = [];

  const list = Array.isArray(messages) ? messages : [];
  for (const msg of list) {
    if (!msg || msg.type === "deleted") continue;
    const senderId = String(msg.user?.id ?? "");
    const senderName =
      selfId && senderId === String(selfId)
        ? "You"
        : firstName(msg.user?.name) || msg.user?.name || "Member";
    const date = shortDate(msg.created_at);
    const messageId = msg.id;
    const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];

    for (const att of attachments) {
      if (!att) continue;
      const common = { messageId, sender: senderName, date, createdAt: msg.created_at };

      // SHARE / CARD ATTACHMENTS FIRST (D15 Befund B): a platform card
      // (radar player/shortlist, scout request/recommendation, last-minute
      // listing, friendly request, business card) is NOT media — it renders as
      // its own card in the chat and must NEVER land in the Media grid behind a
      // generic placeholder tile. Classify it out before the image/video checks
      // so a card that happens to carry an image_url can't leak into Media.
      if (isBusinessCard(att)) {
        businessCards.push({
          ...common,
          name:
            att.title ||
            att.name ||
            `${senderName === "You" ? "Your" : senderName} card — contact details`,
        });
      } else if (att.type && KNOWN_CARD_ATTACHMENT_TYPES.has(att.type)) {
        links.push({
          ...common,
          name: cardLinkTitle(att),
          host: "transferpitch.com",
          // A pure card usually has no external URL (it opens in-app); Open/Copy
          // only surface when one is actually present.
          url: att.title_link || att.og_scrape_url || null,
        });
      } else if (isImageAttachment(att)) {
        media.push({
          ...common,
          kind: "image",
          thumb: att.thumb_url || att.image_url || att.asset_url || null,
          name: att.title || att.fallback || "Image",
          url: att.image_url || att.asset_url || null,
        });
      } else if (isVideoAttachment(att)) {
        media.push({
          ...common,
          kind: "video",
          thumb: att.thumb_url || att.image_url || null,
          name: att.title || att.fallback || "Video",
          url: att.asset_url || null,
        });
      } else if (isFileAttachment(att)) {
        documents.push({
          ...common,
          name: att.title || att.fallback || att.name || "Document",
          kindLabel: fileKind(att),
          size: formatFileSize(att.file_size),
          url: att.asset_url || null,
        });
      } else if (att.type === "share" || att.og_scrape_url || att.title_link) {
        // Generic share envelope / OG link preview → a Links entry.
        const url = att.og_scrape_url || att.title_link || null;
        links.push({
          ...common,
          name: att.title || cardLinkTitle(att),
          host: hostOf(url) || "transferpitch.com",
          url,
        });
      }
    }

    // Platform card links pasted as text (no attachment) → Links entries.
    const text = String(msg.text || "");
    const urlMatches = text.match(ABS_URL) || [];
    for (const url of urlMatches) {
      if (isPlatformCardLink(url)) {
        links.push({
          messageId,
          sender: senderName,
          date,
          createdAt: msg.created_at,
          name: "Shared link",
          host: hostOf(url) || "transferpitch.com",
          url,
        });
      }
    }
  }

  // Reverse-chronological within every section (newest first, 5f).
  const byNewest = (a, b) =>
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  media.sort(byNewest);
  documents.sort(byNewest);
  links.sort(byNewest);
  businessCards.sort(byNewest);

  return {
    [SHARED_SECTIONS.MEDIA]: media,
    [SHARED_SECTIONS.DOCUMENTS]: documents,
    [SHARED_SECTIONS.LINKS]: links,
    [SHARED_SECTIONS.BUSINESS_CARDS]: businessCards,
  };
}

function hostOf(url) {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch (e) {
    return null;
  }
}

export const sharedContentIsEmpty = (buckets) =>
  !buckets ||
  ((buckets[SHARED_SECTIONS.MEDIA]?.length || 0) === 0 &&
    (buckets[SHARED_SECTIONS.DOCUMENTS]?.length || 0) === 0 &&
    (buckets[SHARED_SECTIONS.LINKS]?.length || 0) === 0 &&
    (buckets[SHARED_SECTIONS.BUSINESS_CARDS]?.length || 0) === 0);
